/**
 * TaskTimerSection
 *
 * Displays time tracking controls and session history for a task.
 * Shows inside TaskDetail, below the snooze/action area.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Square, Timer } from 'lucide-react';
import clsx from 'clsx';
import {
  startTimer,
  stopTimer,
  getTimeEntries,
  addManualEntry,
  getActiveTimer,
} from '../api/timer';
import type { TimeEntriesResponse } from '../api/timer';

interface TaskTimerSectionProps {
  taskId: string;
  isCompleted: boolean;
}

// ── Format helpers ────────────────────────────────────────────────────────────

function formatSessionDate(isoString: string): string {
  const d = new Date(isoString);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    '  ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

function formatDurationSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTotalTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TaskTimerSection({ taskId, isCompleted }: TaskTimerSectionProps) {
  const [entriesData, setEntriesData] = useState<TimeEntriesResponse | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Manual entry form state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runningStartRef = useRef<Date | null>(null);

  // ── Timer tick ──────────────────────────────────────────────────────────────

  const startTick = useCallback((startedAt: Date) => {
    runningStartRef.current = startedAt;

    // Set initial elapsed immediately
    setElapsedSeconds(Math.floor((Date.now() - startedAt.getTime()) / 1000));

    intervalRef.current = setInterval(() => {
      if (!runningStartRef.current) return;
      setElapsedSeconds(Math.floor((Date.now() - runningStartRef.current.getTime()) / 1000));
    }, 1000);
  }, []);

  const stopTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    runningStartRef.current = null;
    setElapsedSeconds(0);
  }, []);

  // ── Load entries + restore active timer ────────────────────────────────────

  const loadEntries = useCallback(async () => {
    try {
      const data = await getTimeEntries(taskId);
      setEntriesData(data);
    } catch (err) {
      // Non-fatal — entries section will just be empty
      console.error('[TaskTimerSection] Failed to load entries:', err);
    }
  }, [taskId]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      await loadEntries();

      if (isCompleted) return;

      try {
        const active = await getActiveTimer();
        if (cancelled) return;
        if (active && active.taskId === taskId) {
          setIsRunning(true);
          startTick(new Date(active.startedAt));
        }
      } catch (err) {
        // Active timer check is best-effort
        console.error('[TaskTimerSection] Failed to check active timer:', err);
      }
    }

    void init();

    return () => {
      cancelled = true;
      stopTick();
    };
  }, [taskId, isCompleted, loadEntries, startTick, stopTick]);

  // ── Start / Stop handlers ───────────────────────────────────────────────────

  const handleStart = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await startTimer(taskId);
      setIsRunning(true);
      startTick(new Date(result.startedAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start timer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await stopTimer(taskId);
      setIsRunning(false);
      stopTick();
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop timer');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Manual entry handler ────────────────────────────────────────────────────

  const handleAddManual = async () => {
    setManualError(null);
    if (!manualStart || !manualEnd) {
      setManualError('Both start and end times are required');
      return;
    }
    const startDate = new Date(manualStart);
    const endDate = new Date(manualEnd);
    if (endDate <= startDate) {
      setManualError('End time must be after start time');
      return;
    }
    setIsAddingManual(true);
    try {
      await addManualEntry(taskId, startDate.toISOString(), endDate.toISOString());
      setManualStart('');
      setManualEnd('');
      setShowManualForm(false);
      await loadEntries();
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setIsAddingManual(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const entries = entriesData?.entries ?? [];
  const totalSeconds = entriesData?.totalSeconds ?? 0;

  return (
    <div className="mt-4 pt-4 border-t border-border-subtle-light dark:border-border-subtle-dark">
      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <Timer className="w-3.5 h-3.5 text-text-tertiary-light dark:text-text-tertiary-dark" />
        <h4 className="text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary-dark uppercase tracking-wide">
          Time Tracked
        </h4>
      </div>

      {/* Timer button — hidden when task is completed */}
      {!isCompleted && (
        <div className="mb-3">
          {isRunning ? (
            <button
              onClick={handleStop}
              disabled={isLoading}
              className={clsx(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors',
                'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
                'border border-red-200 dark:border-red-800',
                'hover:bg-red-100 dark:hover:bg-red-900/30',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
              aria-label="Stop timer"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
              <span className="font-mono tabular-nums">{formatElapsed(elapsedSeconds)}</span>
              <span>Stop</span>
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={isLoading}
              className={clsx(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors',
                'border border-border-subtle-light dark:border-border-subtle-dark',
                'text-text-primary-light dark:text-text-primary-dark',
                'hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
              aria-label="Start timer"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Start Timer</span>
            </button>
          )}

          {error && (
            <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      )}

      {/* Sessions list */}
      {entries.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-medium text-text-tertiary-light dark:text-text-tertiary-dark mb-1">
            Sessions
          </p>
          <div className="space-y-0.5">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between text-xs text-text-secondary-light dark:text-text-secondary-dark py-1"
              >
                <span className="font-mono">
                  {formatSessionDate(entry.startedAt)}
                  {entry.stoppedAt ? ` – ${new Date(entry.stoppedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ' – running'}
                </span>
                <span className="text-text-tertiary-light dark:text-text-tertiary-dark ml-4 shrink-0">
                  {entry.durationSeconds !== null
                    ? formatDurationSeconds(entry.durationSeconds)
                    : '–'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual entry trigger / form */}
      {!isCompleted && (
        <div className="mb-2">
          {showManualForm ? (
            <div className="mt-2 space-y-2">
              {manualError && (
                <p className="text-xs text-red-600 dark:text-red-400">{manualError}</p>
              )}
              <div className="space-y-1.5">
                <label className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                  Start
                </label>
                <input
                  type="datetime-local"
                  value={manualStart}
                  onChange={(e) => setManualStart(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-raised-dark text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                  End
                </label>
                <input
                  type="datetime-local"
                  value={manualEnd}
                  onChange={(e) => setManualEnd(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-raised-dark text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddManual}
                  disabled={isAddingManual}
                  className="px-3 py-1 text-xs font-medium text-white bg-primary-700 hover:bg-primary-800 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowManualForm(false);
                    setManualStart('');
                    setManualEnd('');
                    setManualError(null);
                  }}
                  className="px-3 py-1 text-xs text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowManualForm(true)}
              className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark transition-colors"
            >
              + Add manual entry
            </button>
          )}
        </div>
      )}

      {/* Total */}
      <div className="flex items-center justify-between text-xs font-medium text-text-primary-light dark:text-text-primary-dark pt-2 border-t border-border-subtle-light dark:border-border-subtle-dark mt-1">
        <span>Total</span>
        <span className="font-mono">
          {totalSeconds > 0 ? formatTotalTime(totalSeconds) : '–'}
        </span>
      </div>
    </div>
  );
}
